import React, { useState } from "react";
import { Mail, Lock, LogIn, UserPlus, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { signInRequest, registerRequest, forgotPassword, resetPassword } from "../../api";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full bg-black border border-white/20 rounded-md pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500";

const SignInView = ({ onAuthed, resetToken }) => {
  const [mode, setMode] = useState(resetToken ? "reset" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgotResult, setForgotResult] = useState(null);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setForgotResult(null);
    setPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (mode === "signin") {
        const res = await signInRequest(email.trim(), password);
        toast.success(`Welcome back, ${res.user.email}`);
        onAuthed(res);
      } else if (mode === "register") {
        const res = await registerRequest(fullName.trim(), email.trim(), password);
        toast.success("Account created — welcome!");
        onAuthed(res);
      } else if (mode === "forgot") {
        const res = await forgotPassword(email.trim());
        setForgotResult(res);
      } else if (mode === "reset") {
        const res = await resetPassword(resetToken, password);
        toast.success(res.message);
        window.history.replaceState({}, document.title, "/");
        switchMode("signin");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response && err.response.data && err.response.data.detail) || "Request failed");
    }
    setBusy(false);
  };

  const titles = {
    signin: ["Sign in to PnLFlow", "Enter your email and password to access your portal."],
    register: ["Create your account", "Set up an account to submit and track your service requests."],
    forgot: ["Reset your password", "Enter your email and we'll send you a secure reset link."],
    reset: ["Choose a new password", "Enter a new password for your account (min 8 characters)."],
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8" data-testid="view-signin">
      <div className="glass-card rounded-md border border-white/10 max-w-md w-full p-8 space-y-6">
        <div className="space-y-2">
          <div className="w-10 h-10 rounded-md bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
            {mode === "forgot" || mode === "reset" ? <KeyRound className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
          </div>
          <h1 className="text-2xl font-black text-white">{titles[mode][0]}</h1>
          <p className="text-xs text-zinc-400 leading-relaxed">{titles[mode][1]}</p>
        </div>

        {(mode === "signin" || mode === "register") && (
          <div className="flex bg-white/5 border border-white/10 rounded-md p-1 text-xs font-semibold">
            <button
              data-testid="auth-tab-signin"
              onClick={() => switchMode("signin")}
              className={`flex-1 py-1.5 rounded-md transition ${mode === "signin" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => switchMode("register")}
              className={`flex-1 py-1.5 rounded-md transition ${mode === "register" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              Create Account
            </button>
          </div>
        )}

        {mode === "forgot" && forgotResult ? (
          <div className="space-y-4" data-testid="forgot-sent-panel">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 text-xs text-emerald-400 leading-relaxed">
              {forgotResult.message}
            </div>
            {forgotResult.dev_reset_link && (
              <button
                data-testid="forgot-dev-link-btn"
                onClick={() => { window.location.href = window.location.origin + forgotResult.dev_reset_link; }}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold py-2.5 rounded-md transition-colors"
              >
                Open reset link (internal test account)
              </button>
            )}
            <button data-testid="back-to-signin-btn" onClick={() => switchMode("signin")} className="w-full text-xs text-zinc-400 hover:text-white transition-colors flex items-center justify-center space-x-1">
              <ArrowLeft className="w-3 h-3" />
              <span>Back to sign in</span>
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="relative">
                <UserPlus className="w-4 h-4 text-zinc-500 absolute left-3 top-3" />
                <input required minLength={2} data-testid="register-name-input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" className={inputCls} />
              </div>
            )}
            {mode !== "reset" && (
              <div className="relative">
                <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-3" />
                <input type="email" required data-testid="signin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
              </div>
            )}
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="w-4 h-4 text-zinc-500 absolute left-3 top-3" />
                <input
                  type="password"
                  required
                  minLength={mode === "signin" ? 1 : 8}
                  data-testid="signin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signin" ? "Password" : "New password (min 8 characters)"}
                  className={inputCls}
                />
              </div>
            )}
            {error && (
              <div className="text-[11px] text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="signin-submit-btn"
              className="w-full bg-white hover:bg-zinc-200 disabled:opacity-50 text-black font-bold py-2.5 rounded-md text-sm transition-colors flex items-center justify-center space-x-2"
            >
              <LogIn className="w-4 h-4" />
              <span>
                {busy ? "Please wait…" : mode === "signin" ? "Sign In" : mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Set New Password"}
              </span>
            </button>
            {mode === "signin" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")} className="w-full text-[11px] text-zinc-400 hover:text-white transition-colors">
                Forgot your password?
              </button>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <button type="button" data-testid="back-to-signin-btn" onClick={() => { window.history.replaceState({}, document.title, "/"); switchMode("signin"); }} className="w-full text-[11px] text-zinc-400 hover:text-white transition-colors flex items-center justify-center space-x-1">
                <ArrowLeft className="w-3 h-3" />
                <span>Back to sign in</span>
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default SignInView;
